import DeleteIcon from '@mui/icons-material/Delete'
import EditNoteIcon from '@mui/icons-material/EditNote'
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import {
  GridApiPremium,
  GridFooterContainer,
  GridPagination,
  GridRowSelectionModel,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import React, { ComponentPropsWithoutRef, FC } from 'react'
import { Deal, OrganizationTableItem } from 'types'
import DealsPopover from '../DealsPopover'

interface DealsGridFooterProps extends ComponentPropsWithoutRef<'div'> {
  rowSelectionModel: GridRowSelectionModel
  deals: Deal[] | OrganizationTableItem[]
  anchorEl: HTMLElement | null
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  onClickDelete: (
    rowSelectionModel: GridRowSelectionModel,
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
    apiRef: React.RefObject<GridApiPremium>
  ) => void
  handleConfirmDelete: (rowSelectionModel: GridRowSelectionModel) => void
  handleClosePopover: (rowSelectionModel: GridRowSelectionModel) => void
  handleEditClick: () => void
  handleClone: (mode: string) => void
  selectedData: any
  apiRef: React.RefObject<GridApiPremium>
  serverSideMode?: boolean
}

const tooltipGridFooter = (title: string) => {
  return <Typography>{title}</Typography>
}

const determineCloneAndReplaceTooltip = (title: string, hasPredecessor: boolean, isDealWon: boolean) => {
  return !hasPredecessor && isDealWon ? (
    <Typography>{title}</Typography>
  ) : (
    <>
      {hasPredecessor && <Typography sx={{ color: 'text.error' }}>Deal already used for clone & replace</Typography>}
      {!isDealWon && (
        <Typography sx={{ color: 'text.error' }}>Deal must be in deal won stage to clone & replace</Typography>
      )}
    </>
  )
}

export const DealsGridFooter: FC<DealsGridFooterProps> = ({
  rowSelectionModel,
  deals: _deals,
  anchorEl,
  setAnchorEl,
  onClickDelete,
  handleConfirmDelete,
  handleClosePopover,
  handleEditClick,
  handleClone,
  selectedData,
  apiRef,
  serverSideMode = false,
}) => {
  const gridApiContext = useGridApiContext()
  const open = Boolean(anchorEl)
  const id = open ? 'grid-footer-popover' : undefined
  const hasPredecessor = selectedData?.successorId && selectedData?.successorId !== '' ? true : false
  const isDealWon = selectedData?.stage === 'Deal Won'
  const hasSelection = React.useMemo(() => Array.from(rowSelectionModel.ids).length > 0, [rowSelectionModel])

  // Get row counts for display
  const rowCount = gridApiContext.current?.getRowsCount?.() ?? 0
  const visibleRowCount = gridApiContext.current?.state?.pagination?.paginationModel?.pageSize ?? rowCount

  const confirmDelete = () => {
    handleConfirmDelete(rowSelectionModel)
  }

  const closePopover = () => {
    handleClosePopover(rowSelectionModel)
  }

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 52,
        overflow: 'hidden',
        px: 2,
      }}
    >
      {hasSelection ? (
        <>
          {/* Left: Selection info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
            <Typography>{Array.from(rowSelectionModel.ids).length === 1 ? 'Edit' : 'Update'}</Typography>
            <Chip label={rowSelectionModel.ids.size} sx={{ color: 'rgb(24, 144, 255)', fontSize: 18 }} />
            <Typography> {Array.from(rowSelectionModel.ids).length === 1 ? 'Deal' : 'Deals'}</Typography>
          </Box>

          {/* Right: Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1 }}>
            {selectedData && Array.from(rowSelectionModel.ids).length === 1 && (
              <>
                {selectedData?.semanticVersion ? (
                  <Chip label={`v${selectedData.semanticVersion}`} size="small" sx={{ color: 'text.secondary' }} />
                ) : null}
                {selectedData.stage !== 'Deal Won' ? (
                  <Tooltip title={tooltipGridFooter('Edit')} placement="top">
                    <IconButton onClick={handleEditClick} color="inherit">
                      <EditNoteIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {/* <Tooltip title={tooltipGridFooter('Clone')} placement="top">
                  <IconButton onClick={() => handleClone('clone')} color="inherit" sx={{ height: 45, width: 45 }}>
                    <Typography sx={{ fontSize: 30 }}>🧬</Typography>
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={determineCloneAndReplaceTooltip('Clone & Replace', hasPredecessor, isDealWon)}
                  placement="top"
                >
                  <div>
                    <IconButton
                      onClick={() => handleClone('replace')}
                      disabled={hasPredecessor || !isDealWon}
                      color="inherit"
                      sx={{ height: 45, width: 45 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 30,
                          color: hasPredecessor || !isDealWon ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        ⇄
                      </Typography>
                    </IconButton>
                  </div>
                </Tooltip>
 */}
                <Tooltip title={tooltipGridFooter('Delete')} placement="top">
                  <IconButton onClick={() => onClickDelete(rowSelectionModel, setAnchorEl, apiRef)} color="inherit">
                    <DeleteIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip
              title={
                Array.from(rowSelectionModel.ids).length === 1
                  ? 'Update functionality in progress...'
                  : 'Mass update functionality in progress...'
              }
              placement="top"
            >
              <Box>
                <Button color="secondary" variant="contained" disabled={true} sx={{ height: 40 }}>
                  {Array.from(rowSelectionModel.ids).length === 1 ? 'Update' : 'Mass Update'}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </>
      ) : (
        <>
          {/* Left spacer */}
          <Box />
          {/* Right: Pagination controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
            {serverSideMode && <GridPagination />}
          </Box>
        </>
      )}
      <DealsPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        handleConfirmDelete={confirmDelete}
        selectedData={selectedData}
      />
    </GridFooterContainer>
  )
}
